/**
 * THE TASK WIRE TYPES AND THE DAY HELPERS (V0.50.2) — pure, DOM-free, clock-free.
 *
 * THE WORD IS "TASK", THE CODE SAYS "TODO" (V0.51.1). Everything a person reads says task;
 * every identifier here and everywhere else keeps `todo` — this file's name, the `Todo` type,
 * the tables, `VIEW_TODOS`/`EDIT_TODOS`, `/api/todos*`, the `todo.*` audit keys, the CSS
 * classes. UI-only because a large part of those places are STORED: the two permission values
 * sit inside every `Role.roles` row and the audit keys inside every historical event, so a
 * full rename would mean migrating values under live rows in exchange for nothing a reader
 * sees. French and German already said tâche and Aufgabe. `i18n/todos.ts` and `PERMISSIONS.md`
 * §1 state the same rule where their own readers will look for it.
 *
 * Moved here from `api/todos.ts` (the types) and `features/popup/TodoSection.tsx` (the helpers)
 * when the Projects tab needed both inside a library the e2e suite can import to recompute its
 * expectations — the `lib/docTable.ts` ↔ `api/docs.ts` arrangement: the types are declared
 * here, `api/todos.ts` re-exports them, and the suite's compiler never reaches Vite-typed code.
 *
 * TODAY IS ALWAYS A PARAMETER. `lib/health.ts` states the rule for this directory: nothing here
 * reads the clock, so every answer is a function of its inputs and a test can ask about any
 * day. The features that call these bind `todayKey()` at the call site (`TodoSection.tsx` keeps
 * the two-argument `isOverdue(due, state)` it always had as a BOUND wrapper, not a copy).
 *
 * DAYS ARE STRINGS. `YYYY-MM-DD`, compared as strings — which sort lexicographically as dates —
 * and never through `Date`: going through `Date` is how a task due today becomes "overdue" for
 * everybody in a negative UTC offset. The one place a `Date` appears is turning an INSTANT
 * (`createdAt`, `completedAt`) into the LOCAL day it fell on, because that is the calendar the
 * reader is looking at and the one their `dueDate`s are written in.
 */

export type TodoState = 'open' | 'done';

export interface Todo {
  id: string;
  nodeId: string;
  title: string;
  notes: string | null;
  state: TodoState;
  /** Everybody it is aimed at. Two flat lists — a person and a team are drawn differently. */
  assigneeUserIds: string[];
  assigneeGroupIds: string[];
  /** `YYYY-MM-DD`, or null. A DAY — see the header. */
  dueDate: string | null;
  position: number;
  /** The task this is a sub-task OF (V0.51.1), or null. ONE level: a child never has children. */
  parentId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  completedBy: string | null;
  completedAt: string | null;
}

/** One of MY to-dos: the row plus how it reached me and enough node context to draw it. */
export interface MyTodo extends Todo {
  /** null when it is aimed at me personally, the team id when it reaches me through my team. */
  via: string | null;
  /**
   * The title of the job this is one step of (V0.51.1), or null for a task in its own right.
   *
   * SHIPPED, not resolved — the reason `nodeLabel` is: the parent is usually somebody else's
   * row and is not in this list, so there is nothing here to look it up in. Without it a
   * sub-task reads as a fragment.
   */
  parentTitle: string | null;
  nodeLabel: string | null;
  nodeColor: string | null;
}

/** What a create or an edit may carry. `state` is deliberately absent — ticking is its own call. */
export interface TodoDraft {
  title?: string;
  notes?: string | null;
  dueDate?: string | null;
  /**
   * CREATE ONLY (V0.51.1): the parent this new sub-task belongs to. Moving an existing task
   * under a parent goes through the re-order route, because a move is a change of place as
   * much as of ownership and this shape has no way to say where it lands.
   */
  parentId?: string | null;
  /** NAMING EITHER FAMILY REPLACES BOTH — a total write, so taking somebody off is sayable. */
  assigneeUserIds?: string[];
  assigneeGroupIds?: string[];
}

/**
 * A node marked as a PROJECT (V0.50.2): the node's subtree is the scope, the to-dos on it and
 * beneath it are the work, and this is the plan — two optional DAYS. "Is a project" is the row
 * existing; there is no flag.
 */
export interface ProjectRow {
  nodeId: string;
  startDate: string | null;
  /** The target day — "target" in the UI, `dueDate` on the wire for parity with a to-do's. */
  dueDate: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The whole plan, both keys always present: the server takes a TOTAL write. */
export interface ProjectDraft {
  startDate: string | null;
  dueDate: string | null;
}

/** `GET /api/projects` — facts only; every rollup is computed here, in `lib/projects.ts`. */
export interface ProjectsPayload {
  projects: ProjectRow[];
  todos: Todo[];
}

/** `GET /api/nodes/:id/todos` — the list, and whether the node is a project, in one fetch. */
export interface NodeTodosPayload {
  todos: Todo[];
  project: ProjectRow | null;
}

/* ------------------------------------------------------------------------------- days */

export const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

const pad2 = (n: number) => String(n).padStart(2, '0');

/** The LOCAL calendar day an instant falls on, as `YYYY-MM-DD`. Takes the Date; never reads the clock. */
export function dayKeyOf(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** An ISO timestamp -> the local day it fell on. */
export const isoDayKey = (iso: string): string => dayKeyOf(new Date(iso));

/** `day` shifted by `n` days (negative allowed). Pure calendar arithmetic through UTC, so a DST
 *  change in the reader's zone cannot make a day 23 or 25 hours long here. */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Whole days from `a` to `b`; negative when `b` is before `a`. */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/**
 * How a due date reads: overdue is OPEN and STRICTLY before today. Due today is not overdue.
 * String comparison — see the header.
 */
export const isOverdue = (due: string | null, state: string, today: string): boolean =>
  !!due && state !== 'done' && due < today;

/** `2026-09-19` -> `19 Sep`, or the year too when it is not today's. */
export function formatDue(due: string, today: string): string {
  const [y, m, d] = due.split('-');
  return `${Number(d)} ${MONTHS_SHORT[Number(m) - 1]}${y === today.slice(0, 4) ? '' : ' ' + y}`;
}

/** Has this to-do got anything to expand? The ONE place that decides, used on every surface. */
export const hasNotes = (t: { notes: string | null }): boolean => !!t.notes && !!t.notes.trim();

/* --------------------------------------------------------------------- the hierarchy */

/**
 * SUB-TASKS (V0.51.1) — the pure half.
 *
 * ONE LEVEL, and these functions assume it rather than enforce it: the server does that
 * (`server/src/todos/hierarchy.ts`), and a client that re-derived the rule would be a second
 * copy free to disagree. What lives here is the SHAPE the list is drawn in and the arithmetic
 * a drag needs, both of which the popup and the suite have to agree on exactly.
 */

/** Every sub-task of `parentId`, in the order somebody arranged them. `null` gives the roots. */
export const childrenOf = (todos: readonly Todo[], parentId: string | null): Todo[] =>
  todos.filter((t) => (t.parentId ?? null) === parentId).sort((a, b) => a.position - b.position);

/** The tasks that are nobody's sub-task. */
export const rootsOf = (todos: readonly Todo[]): Todo[] => childrenOf(todos, null);

export const hasChildren = (todos: readonly Todo[], id: string): boolean =>
  todos.some((t) => t.parentId === id);

/** How far along a parent is: `{ done, total }` over its children. `{0,0}` for a childless task. */
export function rollupOf(todos: readonly Todo[], id: string): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const t of todos) {
    if (t.parentId !== id) continue;
    total += 1;
    if (t.state === 'done') done += 1;
  }
  return { done, total };
}

/**
 * Done rows sink, and everything else keeps the order somebody arranged.
 *
 * MOVED HERE from `TodoSection.tsx` (V0.51.1), where it had been the section's own and had
 * grown a second copy inside the Projects tab's cells. One implementation, because the two
 * surfaces draw the same list and a list that sorts differently in two places is a bug nobody
 * reports — they just stop trusting the order.
 *
 * SORTED HERE RATHER THAN BY THE SERVER, still: `position` is a human arrangement, and a server
 * that re-sorted on state would destroy it the first time a row was ticked and un-ticked. The
 * sink is presentation, so it lives where presentation does. WITHIN a sibling group only —
 * a done sub-task sinks past its siblings, never out of its parent.
 */
export const inDisplayOrder = <T extends { state: string; position: number }>(todos: readonly T[]): T[] =>
  [...todos].sort((a, b) => {
    const ad = a.state === 'done' ? 1 : 0;
    const bd = b.state === 'done' ? 1 : 0;
    return ad - bd || a.position - b.position;
  });

/** One root and the sub-tasks under it, both in display order. */
export interface TaskBranch {
  task: Todo;
  children: Todo[];
}

/**
 * The list as it is DRAWN: roots in display order, each carrying its own children in theirs.
 *
 * An orphan — a sub-task whose parent is not in this list — is drawn as a ROOT rather than
 * dropped. It should not happen (the server ships a node's whole list), but a row that vanishes
 * because of a referential accident is the worst of the available failures: the work is still
 * assigned to somebody and they can no longer see it.
 */
export function displayTree(todos: readonly Todo[]): TaskBranch[] {
  const present = new Set(todos.map((t) => t.id));
  const roots = todos.filter((t) => !t.parentId || !present.has(t.parentId));
  return inDisplayOrder(roots).map((task) => ({
    task,
    children: inDisplayOrder(todos.filter((t) => t.parentId === task.id)),
  }));
}

/**
 * The whole arrangement, flattened into what `PUT /api/nodes/:id/todos/order` takes.
 *
 * Every task named once, parents before their children, and each mapped to its parent — the
 * two halves the route checks against each other. Built from the tree the person is looking at
 * rather than from the drag's own arithmetic, so what is sent is always exactly what is on
 * screen; a delta computed separately is how the two drift apart.
 */
export function flattenArrangement(tree: readonly TaskBranch[]): {
  ids: string[];
  parents: Record<string, string | null>;
} {
  const ids: string[] = [];
  const parents: Record<string, string | null> = {};
  for (const branch of tree) {
    ids.push(branch.task.id);
    parents[branch.task.id] = null;
    for (const kid of branch.children) {
      ids.push(kid.id);
      parents[kid.id] = branch.task.id;
    }
  }
  return { ids, parents };
}

/** Where a dragged row would land. `into` makes it a sub-task of the target. */
export type DropWhere = 'before' | 'after' | 'into';

/**
 * Would this drop change nothing?
 *
 * The V0.50 drag lit up every row it passed over, including the two either side of the row
 * being dragged, where letting go did nothing at all. An indicator that promises a move and
 * delivers none is worse than no indicator: it teaches people the feature is unreliable. So
 * the caller draws nothing when this says true.
 *
 * A drop on a different parent is never a no-op even when the position looks unchanged — the
 * row changes hands, which is the whole point of the gesture.
 */
export function isNoopDrop(
  todos: readonly Todo[],
  draggedId: string,
  targetId: string,
  where: DropWhere,
): boolean {
  if (draggedId === targetId) return true;
  const dragged = todos.find((t) => t.id === draggedId);
  const target = todos.find((t) => t.id === targetId);
  if (!dragged || !target) return false;
  if (where === 'into') return dragged.parentId === target.id;
  if ((dragged.parentId ?? null) !== (target.parentId ?? null)) return false;

  const sibs = inDisplayOrder(todos.filter((t) => (t.parentId ?? null) === (target.parentId ?? null)));
  const from = sibs.findIndex((t) => t.id === draggedId);
  const to = sibs.findIndex((t) => t.id === targetId);
  if (from < 0 || to < 0) return false;
  return (where === 'before' && to === from + 1) || (where === 'after' && to === from - 1);
}

/** Where a drag ends: on a row, or in the empty space at the end of a list. */
export type DropTarget = { id: string; where: DropWhere } | { endOf: string | null };

/**
 * The whole arrangement after a drag, or null when the move is not allowed.
 *
 * ONE FUNCTION, PURE, AND IT PRODUCES THE WHOLE LIST rather than a delta. That is what closes
 * the gap the V0.50 drag fell into: the client computed one thing, sent another, and the two
 * had no way to be compared. Here the list is rearranged exactly as it is drawn, then flattened
 * by `flattenArrangement`, so what the server is told is what the person is looking at.
 *
 * A PARENT TAKES ITS CHILDREN WITH IT. Dragging a job moves the job, not its first step.
 *
 * Returns null rather than throwing for a move the one-level rule forbids — a task with
 * children cannot become a child — because the caller's answer is "draw no indicator", not
 * "report an error". The server refuses the same move independently; this is what stops the
 * gesture ever being offered.
 */
export function moveInTree(
  todos: readonly Todo[],
  draggedId: string,
  target: DropTarget,
): { ids: string[]; parents: Record<string, string | null> } | null {
  const tree = displayTree(todos);
  const roots: string[] = tree.map((b) => b.task.id);
  const kids = new Map<string, string[]>(tree.map((b) => [b.task.id, b.children.map((c) => c.id)]));
  const parentOf = new Map<string, string | null>();
  for (const b of tree) {
    parentOf.set(b.task.id, null);
    for (const c of b.children) parentOf.set(c.id, b.task.id);
  }
  if (!parentOf.has(draggedId)) return null;

  const draggedHasChildren = (kids.get(draggedId) ?? []).length > 0;

  /** Would `dragged` end up as a child here? Then it may not carry children of its own. */
  const landingUnder = (parentId: string | null): boolean => parentId !== null;

  // Lift it out of wherever it is. Its own children ride along in `kids`, untouched.
  const from = parentOf.get(draggedId) ?? null;
  if (from === null) roots.splice(roots.indexOf(draggedId), 1);
  else kids.set(from, (kids.get(from) ?? []).filter((id) => id !== draggedId));

  const put = (parentId: string | null, index: number): boolean => {
    if (landingUnder(parentId) && draggedHasChildren) return false;
    const list = parentId === null ? roots : (kids.get(parentId) ?? []);
    list.splice(index < 0 ? list.length : index, 0, draggedId);
    if (parentId !== null) kids.set(parentId, list);
    return true;
  };

  let ok: boolean;
  if ('endOf' in target) {
    ok = put(target.endOf, -1);
  } else if (target.where === 'into') {
    // Nesting is offered on a ROOT only, and never onto itself.
    ok = target.id !== draggedId && (parentOf.get(target.id) ?? null) === null && put(target.id, -1);
  } else {
    const host = parentOf.get(target.id) ?? null;
    const list = host === null ? roots : (kids.get(host) ?? []);
    const at = list.indexOf(target.id);
    if (at < 0) ok = false;
    else ok = put(host, target.where === 'before' ? at : at + 1);
  }
  if (!ok) return null;

  const byId = new Map(todos.map((t) => [t.id, t]));
  const rebuilt: TaskBranch[] = roots.flatMap((id) => {
    const task = byId.get(id);
    if (!task) return [];
    return [
      {
        task,
        children: (kids.get(id) ?? []).flatMap((k) => {
          const kid = byId.get(k);
          return kid ? [kid] : [];
        }),
      },
    ];
  });
  return flattenArrangement(rebuilt);
}

/**
 * Is this sub-task due AFTER the job it belongs to?
 *
 * Flagged, never enforced (the user's decision): the two dates are independent, because a step
 * finishing after its parent's target is a real thing to know about and not a thing to forbid —
 * plans are written before they are understood. Both dates must exist for the question to mean
 * anything, and a finished task is not late for anybody.
 */
export const dueAfterParent = (
  child: Pick<Todo, 'dueDate' | 'state'>,
  parent: Pick<Todo, 'dueDate'> | null | undefined,
): boolean => !!child.dueDate && !!parent?.dueDate && child.state !== 'done' && child.dueDate > parent.dueDate;

/**
 * Due within the next few days, and not yet done — the middle tone between "fine" and "late".
 *
 * Three days rather than the Projects tab's seven: this is one node's list, read while doing
 * the work, and a week of amber on every row says nothing. The tab is a planning surface and
 * answers a different question.
 */
export const DUE_SOON_DAYS = 3;
export const isDueSoon = (due: string | null, state: string, today: string): boolean =>
  !!due && state !== 'done' && due >= today && daysBetween(today, due) <= DUE_SOON_DAYS;

/**
 * `today`, `tomorrow`, `yesterday` — or null for a day far enough away to be worth naming.
 *
 * Returns the KEY rather than the word, because the word is a translation and nothing under
 * `lib/` may hold one. The caller reads it out of `i18n/todos.ts` and falls back to
 * `formatDue`, which is how "Tomorrow" stays "Demain" without this file knowing that.
 */
export function relativeDay(due: string, today: string): 'today' | 'tomorrow' | 'yesterday' | null {
  const n = daysBetween(today, due);
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  if (n === -1) return 'yesterday';
  return null;
}

/**
 * The first word of somebody's display name, for a pill with no room for the rest.
 *
 * The full name rides the tooltip, always. Deliberately naive — one split on whitespace, no
 * attempt to guess which part is a given name — because every clever version of this is wrong
 * for somebody's name, and a plain first token is at least predictable.
 */
export const firstNameOf = (displayName: string): string => displayName.trim().split(/\s+/)[0] || displayName;
